import { BackendApiResponse, DateString, DateTimeString, EntityId, PersonAge } from '@shared/core/types';
import { FormField, FormFieldSerializedValues } from '@shared/shared/form-fields';
import { EServiceTypeKey } from '@skyeng/libs/shared/stk';
import { EducationServiceCorporate } from '@crm/persons/person/education-services/types';
import { SkyPriceCurrency } from '@skyeng/libs/shared/i18n';

export interface EducationServiceCompanyContract {
  id: EntityId;
  number: string;
  company: {
    id: EntityId;
    name: string;
  };
}

export interface IdUser {
  general: {
    id: EntityId;
    name: string;
    surname: string;
  };
}

export interface Person {
  id: EntityId;
  type: PersonType;
  identity: string;
  name: string;
  surname: string;
  phone: string;
  phoneContactUuid: string;
  email: string;
  identityPhone: string;
  identityEmail: string;
  birthday: DateString;
  utcOffset: number;
  utcOffsetInSeconds: number;
  region: string;
  country: string;
  skype: string;
  isForbiddenToCall: boolean;
  isDisabledServiceCommunications: boolean;
  additionalEmail?: string;
  locale: PersonLocale;
  serviceLocale: PersonServiceLocale;
  timezone: string;
  avatarUrl?: string;
  isTest: boolean;
  brand: PersonBrand;
  operatorData: OperatorData;
  class?: string;
}

export interface OperatorData {
  hrm_id: number;
  auth_id: number;
  employee_name: string;
  work_profiles: OperatorWorkProfile[];
}

export interface OperatorWorkProfile {
  is_main: 1 | 0;
  profile_status: number;
  entry_date: string;
  quit_date: string | null;
  position: string;
  head_auth_id: number;
  department_name: string;
  head_name: string;
  department: number;
  all_departments: number[];
}

export enum PersonType {
  student = 'student',
  teacher = 'teacher',
  operator = 'operator',
}

export interface EducationServiceVacationCreateRequest {
  type: VacationType;
  startsAt: DateString;
  endsAt: DateString;
  reason?: string;
}

export interface EducationServiceVacationUpdateRequest {
  startsAt: DateString;
  endsAt: DateString;
}

export enum VacationStatus {
  scheduled = 'scheduled',
  started = 'started',
  ended = 'ended',
  canceled = 'canceled',
  notPlanned = 'notPlanned',
}

export enum VacationType {
  vacation = 'vacation',
  schoolBreak = 'school_break',
}

export type EducationServiceIncorrect =
  | 'moved_to_crm1'
  | 'contact_details_correct'
  | 'other_people_contact_information'
  | 'attempt_to_find_job'
  | 'kid_does_not_communicate'
  | 'customer_wanted_another_service'
  | 'rollback_migration_to_crm1'
  | 'tested_education_service';

export enum EducationServiceStage {
  beforeCall = 'before_call',
  afterCall = 'after_call',
  afterTrial = 'after_trial',
  regularLessons = 'regular_lessons',
  lost = 'lost',
  vacation = 'vacation',
}

export type EducationService = {
  id: EntityId;
  serviceTypeKey: EServiceTypeKey;
  operatorNote: string;
  createdAt?: DateTimeString;
  stage?: EducationServiceStage;
  teacher?: EducationServiceTeacher | null;
  teachers?: EducationServiceTeacher[];
  temporaryTeacher?: EducationServiceTeacher | null;
  paymentAgreement?: EducationServicePaymentAgreement;
  incorrectnessReason?: EducationServiceIncorrect | null;
  corporate: EducationServiceCorporate | null;
  isFirstPaymentReceived: boolean;
  balance: number;
  isTeacherReplacementInProgress?: boolean;
  isOnCrisisSupport?: boolean;
  isBizDev?: boolean;
  businessModel?: string;
  parentEducationServiceId: number;
  stageFromClientDb: string;
  educationServiceKit?: {
    id: number;
  };
};

export type EducationServiceKit = Omit<EducationService, 'serviceTypeKey'> & {
  id: number;
  productKit: {
    id: number;
    code: string;
    description: string;
    title: string;
  };
  educationServices: EducationService[];
  tariffUuid: string;
};

export interface EducationServicePaymentAgreement {
  person: IdUser | null;
  companyContract: EducationServiceCompanyContract | null;
}

export type EducationServiceTeacher = IdUser & {
  id: number;
  email: string;
  phone: string;
  skype: string;
  type?: 'primary' | 'temporary' | 'exam_checker' | 'coach' | 'mentor';
};

export interface EducationServiceVacation {
  id: EntityId;
  type: VacationType;
  state: VacationStatus;
  startsAt: DateString;
  endsAt: DateString;
  reason: string | null;
}

export type EducationServiceVacationsResponse = BackendApiResponse<EducationServiceVacation[]>;

export interface PersonWithAge extends Person {
  age?: PersonAge;
}

export interface StudentEducationService {
  id: EntityId;
  serviceTypeKey: EServiceTypeKey;
  isOnCrisisSupport: boolean;
}

export class CreatePerson {
  name: string;
  surname: string;
  email: string;
  phone: string;
  birthday: DateString;
  utcOffset?: string;
  timezone?: string;
  locale: PersonLocale;
  serviceLocale: PersonServiceLocale;
  brand?: string;
  isTest?: boolean;
}

export class NewPerson {
  id: EntityId;
}

export type NewPersonResponse = BackendApiResponse<NewPerson>;
export type PersonResponse = BackendApiResponse<Person>;

export enum FamilyRelation {
  parent = 'parent',
  kid = 'kid',
}

export interface FamilyMember extends IdUser {
  relation: FamilyRelation;
  isFromCrm1: boolean;
}

export interface TelephonyPhoneNumber {
  id: EntityId;
  name: string;
  surname: string;
  phone: string;
  phoneContactUuid: string;
}

export enum TelephonyCallType {
  crm_task = 'crm_task',
  teachers_schedule_task = 'teachers_schedule_task',
  css_task = 'css_task',
}

export interface TelephonyCallPayload {
  calledUserId: EntityId;
  taskId: EntityId;
  educationServiceId: EntityId;
  taskTypeLabel: string;
}

export enum PdType {
  phone = 'phone',
  email = 'email',
  identityPhone = 'identityPhone',
  identityEmail = 'identityEmail',
  identity = 'identity',
  identityLogin = 'identityLogin',
  additionalEmail = 'additionalEmail',
}

export enum ContactType {
  phone = 'phone',
  email = 'email',
  telegramId = 'telegramId',
  slack = 'slack',
  skype = 'skype',
  socialVk = 'socialVk',
  socialFb = 'socialFb',
  socialOk = 'socialOk',
}

export interface PersonPersonalData {
  value: string;
}

export type PersonPersonalDataResponse = BackendApiResponse<PersonPersonalData>;
export type EducationServiceBalance = number;

export enum PersonLocale {
  en = 'en',
  ru = 'ru',
  uk = 'uk',
  es = 'es',
  it = 'it',
  de = 'de',
  fr = 'fr',
  ko = 'ko',
  ja = 'ja',
  pl = 'pl',
  pt = 'pt',
  tr = 'tr',
  ar = 'ar',
}

export enum PersonServiceLocale {
  en = 'en',
  ru = 'ru',
  uk = 'uk',
  es = 'es',
  it = 'it',
  de = 'de',
  fr = 'fr',
  ko = 'ko',
  ja = 'ja',
  pl = 'pl',
  pt = 'pt',
  tr = 'tr',
  ar = 'ar',
}

export enum PersonBrand {
  osmi = 'osmi',
  skyeng = 'skyeng',
  skysmart = 'skysmart',
  skypro = 'skypro',
}

export interface LessonPriceItem {
  serviceTypeKey: EServiceTypeKey;
  count: number;
  price: number;
  currency: string;
}

export type LessonPriceItemResponse = BackendApiResponse<LessonPriceItem[]>;

export enum TaskStatus {
  new = 'new',
  inProgress = 'in_progress',
}

export interface TaskAggregate {
  task: TaskInfo;
  student: TaskPersonInfo | null;
  paymentAgreement: TaskPaymentAgreement | null;
  educationService: TaskEducationService | null;
  educationServiceKit?: TaskEducationServiceKit | null;
}

export interface TaskPaymentAgreement {
  person: TaskPaymentAgreementUser | null;
  companyContract: EducationServiceCompanyContract | null;
}

interface TaskPaymentAgreementUser extends IdUser {
  phone: string;
  email: string;
  skype: string;
  utcOffset: number;
}

export interface TaskInfo {
  id: EntityId;
  taskType: string;
  taskTypeLabel: string;
  activeonAt: DateTimeString;
  deadlineAt: DateTimeString | null;
  status: TaskStatus;
  weight: number;
  instruction: string;
  educationService: TaskEducationService;
  resultClassifier?: TaskResultClassifierConfig;
  note: string | null; // from overbooking
  contactCount: number | null;
  totalContactCount: number | null;
}

export interface TaskPersonInfo {
  id: EntityId;
  phone: string;
  email: string;
  skype: string;
  utcOffset: number;
}

export interface TaskResultClassifier {
  name: string;
  value: string;
  fields: FormField[];
}

export interface TaskCompleteResult {
  result: string | null;
  resultName: string;
  comment: string;
  resultVariables: FormFieldSerializedValues;
}

export interface TaskEducationService {
  id: EntityId;
  serviceTypeKey: EServiceTypeKey | null;
  operatorNote: string | null;
}

export interface TaskEducationServiceKit {
  id: EntityId;
  operatorNote: string | null;
  productKit: {
    id: number;
    code: string;
    description: string;
    title: string;
    products?: {
      serviceTypeKey: EServiceTypeKey | null;
    }[];
  };
  tariff?: {
    name: string | null;
    description: string | null;
    cost: number | null;
    currency: SkyPriceCurrency;
  };
}

export interface TaskIntroLessonTime {
  startedAt: DateTimeString | null;
}

export interface SpecialProjectExplanatoryNote {
  name: string;
  description: string;
  saleScriptLink: string;
}

export type TaskResultClassifierConfig = TaskResultClassifier[];
export type TaskIntroLessonTimeResponse = BackendApiResponse<TaskIntroLessonTime>;
export type SpecialProjectExplanatoryNoteResponse = BackendApiResponse<SpecialProjectExplanatoryNote>;

export interface DecisionMakerSummary {
  contracts: DecisionMakerContract[];
}

export interface DecisionMakerContract {
  companyId: EntityId;
  contractId: EntityId;
}

export type DecisionMakerSummaryResponse = BackendApiResponse<DecisionMakerSummary>;

export interface ApiItemsResponse<T> {
  success: boolean;
  items: T[];
  error?: string;
}

export interface Reason {
  id: number;
  label: string;
}

export interface CoFinancingData {
  compensationPercentage: number;
  connectedAt: string;
  period: string | null;
  lessonsLimit: number | null;
}

export interface CorporateBillingEducationServiceWithCoFinancing {
  educationServiceId: number;
  serviceTypeKey: string;
  isCoFinancingEnabled: boolean;
  coFinancingData: CoFinancingData | null;
}

export type CorporateBillingGetStudentEducationServicesCoFinancingDataResponse = BackendApiResponse<
  CorporateBillingEducationServiceWithCoFinancing[]
>;

export interface ProductsForMutationResponse {
  stk_list: string[];
  kit_code_list: string[];
}

export interface TariffListByKitCodeResponse {
  tariff_uuid_list: string[];
}
